import { Tooltip } from '@mui/material';
import { OptionallyVisible } from 'components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MerchantInfo, MerchantKyc, MerchantPayoutFlow, PayoutDestinationType } from 'services/merchants/types';

import { Button } from 'components/buttons';
import { Panel } from 'components/containers';
import DataTable from 'components/dataTable/dataTable';
import IconChat from 'components/icons/chat';
import IconCross from 'components/icons/cross';
import { ModalControls } from 'components/modal/styles';

import { BankAccountCellWithProps } from './components/cells';
import PayoutModal from './components/payoutModal';
import { PayoutProvider } from './components/payoutModal/context';
import { PayoutSetupStepProvider } from './components/payoutModal/context/modal';
import Memo from './components/payoutModal/fiat/memo';
import { LABELS as MODAL_LABELS } from './components/payoutModal/keys';
import { ModalContent, StyledDialog } from './components/payoutModal/styles';
import { SettlementTablePlaceholder } from './components/tablePlaceholder/tablePlaceholder';
import { KEYS, LABELS, PAYOUT_COLUMNS } from './keys';
import { EditMemoButton, MemoControls, MemoPreview, SecondaryText, SettlementContainer, StyledTooltip } from './styles';
import { PayoutDestinationsDataRow } from './types';

interface PaymentMethodsSectionProps {
  payoutDestinations: PayoutDestinationsDataRow[];
  loading: boolean;
  isPartner: boolean;
  merchant: MerchantInfo;
  kyc?: MerchantKyc;
  merchantPayoutFlow: MerchantPayoutFlow;
  payoutMemo: string | null;
}

export const Settlement = ({
  payoutDestinations,
  loading,
  merchant,
  isPartner,
  kyc,
  merchantPayoutFlow,
  payoutMemo,
}: PaymentMethodsSectionProps) => {
  const { t } = useTranslation();

  const [isPayoutModalOpen, setPayoutModalOpen] = useState(false);
  const [isEditingMemo, setIsEditingMemo] = useState(false);

  const mainPayoutDestination = payoutDestinations.find((destination) => destination.isMain);

  const [editedPayout, setEditedPayout] = useState<PayoutDestinationsDataRow | null>(null);
  const isDirectPaymentFlow = merchantPayoutFlow === MerchantPayoutFlow.DIRECT;
  const shouldShowMemoPreview = mainPayoutDestination?.type !== PayoutDestinationType.Crypto && !loading;
  const isEditMemoAvailable = Boolean(mainPayoutDestination) && shouldShowMemoPreview;

  const onPayoutModalClose = () => {
    setPayoutModalOpen(false);
    setEditedPayout(null);
  };

  const openPayoutSetupModal = () => {
    setPayoutModalOpen(true);
  };

  const onEditMemo = () => {
    setIsEditingMemo(true);
  };

  const onEditMemoFinish = () => {
    setIsEditingMemo(false);
  };

  const truncateMemoToMaxLength = (memo: string | null, maxLength: number = 100): string => {
    if (!memo) {
      return '';
    }
    return memo.length > maxLength ? `${memo.slice(0, maxLength)}...` : memo;
  };

  const isMemoEnoughLongToTruncate = () => {
    if (!payoutMemo) {
      return false;
    }
    return payoutMemo?.length > KEYS.MAX_MEMO_PREVIEW_LENGTH;
  };

  const onEditPayoutClick = (row: PayoutDestinationsDataRow) => {
    setEditedPayout(row);
    setPayoutModalOpen(true);
  };

  return (
    <Panel label={t(LABELS.PAYOUT.PANEL_TITLE)}>
      <MemoControls>
        <OptionallyVisible visible={shouldShowMemoPreview}>
          <MemoPreview>
            <IconChat /> <SecondaryText>{t(LABELS.PAYOUT.MEMO, { nsSeparator: false })}</SecondaryText>
            <OptionallyVisible visible={isMemoEnoughLongToTruncate()}>
              <Tooltip slots={{ tooltip: StyledTooltip }} placement="top-start" title={payoutMemo}>
                <span>{truncateMemoToMaxLength(payoutMemo, KEYS.MAX_MEMO_PREVIEW_LENGTH)}</span>
              </Tooltip>
            </OptionallyVisible>
            <OptionallyVisible visible={!isMemoEnoughLongToTruncate()}>
              <span>{payoutMemo || KEYS.NO_MEMO_PLACHEHOLDER}</span>
            </OptionallyVisible>
          </MemoPreview>
        </OptionallyVisible>
        <OptionallyVisible visible={isEditMemoAvailable}>
          <EditMemoButton flat onClick={onEditMemo}>
            <IconChat />
            {t(LABELS.PAYOUT.EDIT_MEMO)}
          </EditMemoButton>
        </OptionallyVisible>
      </MemoControls>
      <SettlementContainer isPartner={isPartner}>
        <OptionallyVisible visible={isPayoutModalOpen}>
          <PayoutProvider merchant={merchant} editing={editedPayout} isDirectPaymentFlow={isDirectPaymentFlow}>
            <PayoutSetupStepProvider
              editing={Boolean(editedPayout)}
              onClose={onPayoutModalClose}
              merchantPayoutFlow={merchantPayoutFlow}
              payoutDestinations={payoutDestinations}
            >
              <PayoutModal isOpen={isPayoutModalOpen} onClose={onPayoutModalClose} payoutDestinations={payoutDestinations} />
            </PayoutSetupStepProvider>
          </PayoutProvider>
        </OptionallyVisible>
        <DataTable
          placeholder={<SettlementTablePlaceholder action={openPayoutSetupModal} kyc={kyc} />}
          loading={loading}
          columns={PAYOUT_COLUMNS()}
          rows={payoutDestinations}
          CellComponent={BankAccountCellWithProps(onEditPayoutClick, merchantPayoutFlow === MerchantPayoutFlow.DIRECT)}
        />
      </SettlementContainer>

      <OptionallyVisible visible={isEditingMemo && Boolean(mainPayoutDestination)}>
        <StyledDialog open={isEditingMemo} scroll="body" disableEscapeKeyDown fullWidth>
          <Panel
            label={t(MODAL_LABELS.MODAL_TITLE)}
            controls={
              <ModalControls>
                <Button flat onClick={onEditMemoFinish}>
                  <IconCross />
                </Button>
              </ModalControls>
            }
          >
            <ModalContent>
              <Memo
                initialValue={payoutMemo ?? undefined}
                skippable={false}
                merchant={merchant}
                paymentRail={mainPayoutDestination?.paymentRail}
                proceed={onEditMemoFinish}
                onClose={onEditMemoFinish}
              />
            </ModalContent>
          </Panel>
        </StyledDialog>
      </OptionallyVisible>
    </Panel>
  );
};
